import { UIMessage } from "ai";
import z from "zod/v4";

import { AiRoleList } from "../../functions/meta";

import { InputRequirementSchema } from "./nodes/input-requirement";
import { UIMessageSchema } from "./context-schemas";
import { ExecutionErrorSchema } from "./node-execution";
import { SessionStatusSchema } from "./session";
import { UIMessageStreamPartSchema } from "./ui-message-stream-part";
import { WorkflowEventTypeSchema } from "./workflow-event";

export const ConversationNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  streamParts: z.array(UIMessageStreamPartSchema).default([]),
  status: SessionStatusSchema.optional(),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
});

export type ConversationNode = z.infer<typeof ConversationNodeSchema>;

export const ConversationMessageSchema = z.object({
  id: z.string(),
  role: z.enum(AiRoleList),
  timestamp: z.number(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  nodes: z.array(ConversationNodeSchema).default([]),
});

export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const ConversationStateSchema = z.object({
  sessionId: z.string(),
  workflowId: z.string(),
  status: SessionStatusSchema,
  messages: z.array(ConversationMessageSchema),
  nextAction: InputRequirementSchema.optional(),
  error: ExecutionErrorSchema.optional(),
  isStreaming: z.boolean().default(false),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
  nodeExecutionStatus: z
    .record(
      z.string(),
      z.object({
        status: SessionStatusSchema,
        startTime: z.number().optional(),
        endTime: z.number().optional(),
        error: z.string().optional(),
      })
    )
    .default({}),
  workflowStartTime: z.number().optional(),
  workflowEndTime: z.number().optional(),
  currentMessageId: z.string().optional(),
});

export type ConversationState = z.infer<typeof ConversationStateSchema>;

export const ConversationActionSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("initialize"),

    nodeId: z.string(),
    sessionId: z.string(),
    workflowId: z.string(),
  }),
  z.object({
    type: z.literal("addMessage"),
    nodeId: z.string(),
    messageId: z.string(),
    message: UIMessageSchema,
  }),
  z.object({
    type: z.literal("addNodeToMessage"),
    nodeId: z.string(),
    nodeName: z.string(),
    messageId: z.string(),
  }),
  z.object({
    type: z.literal("addStreamPart"),
    nodeId: z.string(),
    messageId: z.string(),
    nodeName: z.string(),
    streamPart: UIMessageStreamPartSchema,
  }),
  z.object({
    type: z.literal("setStatus"),
    nodeId: z.string(),
    message: z.string(),
    status: SessionStatusSchema,
  }),
  z.object({
    type: z.literal("setNextAction"),
    nextAction: InputRequirementSchema,
  }),
  z.object({
    type: z.literal("setError"),
    message: z.string(),
    nodeId: z.string(),
    nodeName: z.string(),
  }),
  z.object({
    type: z.literal("setStreaming"),
    isStreaming: z.boolean(),
    nodeName: z.string(),
  }),
  z.object({
    type: z.literal("setEventType"),
    eventType: WorkflowEventTypeSchema,
  }),
  z.object({
    type: z.literal("updateMetadata"),
    metadata: z.record(z.string(), z.unknown()),
  }),
  z.object({
    type: z.literal("start"),
  }),
  z.object({
    type: z.literal("finish"),
  }),
  z.object({
    type: z.literal("reset"),
  }),
]);

export type ConversationAction = z.infer<typeof ConversationActionSchema>;

export interface ConversationHooks {
  onText?: (message: string) => void;
  onMessage?: (message: ConversationMessage) => void;
}
